import type { ThemeConfig } from "antd";
import { theme } from "antd";

export const mbipPalette = {
  metzDeep: "#00517c",
  metzBlue: "#0078a8",
  metzCyan: "#00abe0",
  accent: "#0078a8",
  accentStrong: "#005f8e",
  success: "#12805c",
  warning: "#9a5a08",
  danger: "#bd3d4b",
  info: "#0078a8",
  layout: "#f4f6f8",
  surface: "#ffffff",
  surfaceMuted: "#f7f8fa",
  border: "#d9dee7",
  divider: "#e7eaf0",
  text: "#202633",
  textSecondary: "#596273",
  textTertiary: "#667085",
} as const;

export const mbipTheme: ThemeConfig = {
  // Explicit component density produces a calmer result than applying the
  // compact algorithm to every part of the application chrome.
  algorithm: theme.defaultAlgorithm,
  cssVar: { prefix: "mbip", key: "enterprise-baseline" },
  token: {
    colorPrimary: mbipPalette.accent,
    colorInfo: mbipPalette.info,
    colorSuccess: mbipPalette.success,
    colorWarning: mbipPalette.warning,
    colorError: mbipPalette.danger,
    colorBgBase: mbipPalette.surface,
    colorBgLayout: mbipPalette.layout,
    colorBgContainer: mbipPalette.surface,
    colorFillAlter: mbipPalette.surfaceMuted,
    colorBorder: mbipPalette.border,
    colorBorderSecondary: mbipPalette.divider,
    colorText: mbipPalette.text,
    colorTextSecondary: mbipPalette.textSecondary,
    colorTextTertiary: mbipPalette.textTertiary,
    colorPrimaryBg: "#eaf7fc",
    colorPrimaryBgHover: "#dff2fa",
    controlOutline: "rgba(0, 120, 168, 0.24)",
    borderRadius: 6,
    borderRadiusLG: 8,
    controlHeight: 32,
    fontFamily:
      "Inter, 'PingFang SC', 'Microsoft YaHei', ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontSize: 13,
    lineHeight: 1.5,
    motionDurationFast: "0.1s",
    motionDurationMid: "0.15s",
    motionDurationSlow: "0.2s",
    boxShadowSecondary: "0 8px 24px rgba(27, 36, 51, 0.07)",
  },
  components: {
    Button: {
      borderRadius: 7,
      controlHeight: 32,
      fontWeight: 550,
      primaryShadow: "none",
    },
    Card: {
      bodyPadding: 14,
      headerHeight: 40,
      headerFontSize: 13,
    },
    Layout: {
      bodyBg: mbipPalette.layout,
      headerBg: "rgba(255, 255, 255, 0.94)",
      siderBg: mbipPalette.metzDeep,
    },
    Menu: {
      itemBg: "transparent",
      subMenuItemBg: "rgba(231, 243, 248, 0.52)",
      itemColor: "#334b59",
      itemHoverBg: "#eaf5f9",
      itemHoverColor: mbipPalette.metzDeep,
      itemSelectedBg: "#dff2f9",
      itemSelectedColor: mbipPalette.metzDeep,
      itemBorderRadius: 6,
      itemHeight: 32,
      itemMarginBlock: 1,
      itemMarginInline: 6,
      groupTitleFontSize: 11,
    },
    Select: {
      optionSelectedBg: "#eaf7fc",
      optionActiveBg: "#f7f7fb",
    },
    Skeleton: {
      gradientFromColor: "rgba(29, 41, 57, 0.055)",
      gradientToColor: "rgba(29, 41, 57, 0.105)",
    },
    Statistic: {
      contentFontSize: 23,
      titleFontSize: 12,
    },
    Table: {
      cellPaddingBlock: 7,
      cellPaddingInline: 11,
      cellPaddingBlockSM: 6,
      cellPaddingInlineSM: 10,
      headerBg: "#f8f9fb",
      headerColor: mbipPalette.textSecondary,
      headerSplitColor: "transparent",
      headerSortActiveBg: "#e9f5fa",
      headerSortHoverBg: "#f0f8fb",
      fixedHeaderSortActiveBg: "#e9f5fa",
      rowHoverBg: "#f1f8fb",
      rowSelectedBg: "#e6f4fa",
      rowSelectedHoverBg: "#dff1f8",
      borderColor: "#edf0f3",
      footerBg: "#fafbfc",
      stickyScrollBarBg: "rgba(89, 98, 115, 0.35)",
      stickyScrollBarBorderRadius: 4,
    },
    Tabs: {
      inkBarColor: mbipPalette.metzBlue,
      itemActiveColor: mbipPalette.metzDeep,
      itemSelectedColor: mbipPalette.metzDeep,
      titleFontSize: 13,
    },
    Tag: {
      defaultBg: "#f2f4f7",
      defaultColor: "#596273",
      fontSize: 11,
      lineHeight: 20,
    },
    Tooltip: {
      colorBgSpotlight: "#252b38",
      borderRadius: 7,
    },
  },
};
